/**
 * THE CITY. Street graph, blocks, buildings, Plateau triplex staircases, signage geometry and
 * procedural sign textures, street furniture, collision geometry, streaming.
 *
 * OWNER: `world` — see ARCHITECTURE.md §3.
 *
 * Publishes ctx.world (WorldQuery, §4.5) in init(), replacing the flat-plane fallback.
 *
 * Draw-call strategy — this subsystem owns most of the 320-call budget:
 *   · masonry merged per SECTOR (2×2 blocks) × material family        → ~27
 *   · trim / roof / storefront glass merged per sector                → ~27
 *   · carriageway, sidewalk, kerb, lane paint, asphalt patches, yards →   7
 *   · ALL signage in the city, via one 2048² procedural atlas         →   2
 *   · every prop (cones, cars, poles, trees, stairs, balconies …)     →  ~32 InstancedMesh
 *
 * Streaming: props are stored in contiguous per-block ranges and re-packed into the instance
 * buffers whenever the active-block set changes; sector groups are distance-culled. Emits
 * `world:blockStreamed`.
 */

import * as THREE from 'three';
import { TUNING } from './tuning.js';
import { MeshBuilder, PropSet, LodPair, LOD } from './geom.js';
import { StreetGraph } from './graph.js';
import { SignAtlas } from './signs.js';
import { buildStairVariants, buildBalconyVariants } from './staircase.js';
import { layoutBlocks, LotBuilder, SECTOR_DIM } from './buildings.js';
import { posKey } from './layout-key.js';
import { Props } from './props.js';
import { buildChurch, buildMountain, buildSkyline, buildOuterGround } from './landmarks.js';
import { buildBoundary } from './boundary.js';
import { towerMaterials, buildSkylineShells } from './downtown.js';
import { plantForest, buildLookout, buildVistaShells } from './mountain.js';
import { StaticCollision } from './collision.js';

const EMPTY = Object.freeze([]);
const SECTORS = SECTOR_DIM * SECTOR_DIM;
/** Scenarios that want an unusually deep streaming radius. See `_restream`. */
const ESTABLISH_SCENARIOS = new Set(['establish', 'result']);

export class WorldSystem {
  static id = 'world';

  constructor(ctx) {
    this.ctx = ctx;
    this.T = TUNING;
    this.root = null;
    this.query = null;
    this._frozen = false;
    this._streamTick = 0;
    this._streamScale = 1;
    this._weather = 'thaw';
    this._built = false;
    this._materials = [];
    this._textures = [];
    this._geoms = [];
    this.instSets = [];
    this.stairVariants = [];
    this.balconyVariants = [];
    this.stairVariantsFar = [];
    this.balconyVariantsFar = [];
    this._winterSets = null;
    this._summerSets = null;
    this._corniceSnow = [];
    this._shellMeshes = [];
    this._warmFrames = 0;
    this._winterOn = null;
    // Hoisted so the per-frame LOD check allocates nothing (N9 — GC spikes land in p99).
    this._isActive = (b) => this.active[b] === 1;
    this._isNear = (b) => this.near[b] === 1;
    this._lodCamX = Infinity;
    this._lodCamZ = Infinity;
    this._lastRepack = -1e9;
    this._lodPending = false;
  }

  // ================================================================== lifecycle

  async init() {
    this.rng = this.ctx.rng.fork(this.T.rngLabel);
    await this._build();
    this.query = this._makeQuery();
    this.ctx.world = this.query;
    this._restream(true);
  }

  async reset(opts = {}) {
    this._teardown();
    this.rng = this.ctx.rng.fork(this.T.rngLabel);
    await this._build();
    this.query = this._makeQuery();
    this.ctx.world = this.query;
    this._streamScale = ESTABLISH_SCENARIOS.has(opts.scenario) ? this.T.stream.establishScale : 1;
    this._restream(true);
  }

  // ================================================================== build

  async _build() {
    const ctx = this.ctx;
    const T = this.T;
    const rngGeo = this.rng.fork('geometry');
    const rngProp = this.rng.fork('props');
    const rngSign = this.rng.fork('signs');

    this.root = new THREE.Group();
    this.root.name = 'world';
    ctx.scene.add(this.root);
    this._corniceSnow = [];
    this._winterOn = null;

    this.graph = new StreetGraph(T);

    // --- materials --------------------------------------------------------
    const M = ctx.materials;
    const vc = (key, name) => {
      const m = M.instanceOf(key);
      m.vertexColors = true;
      m.name = 'world:' + (name || key);
      this._materials.push(m);
      return m;
    };
    const decal = (key, colored) => {
      const m = M.instanceOf(key);
      if (colored) m.vertexColors = true;
      m.polygonOffset = true;
      m.polygonOffsetFactor = -2;
      m.polygonOffsetUnits = -4;
      m.name = 'world:' + key;
      this._materials.push(m);
      return m;
    };

    /**
     * ROUND-4 PROGRAM BUDGET (§9: ≤ 45, measured 50). A `vc()` clone turns vertexColors ON,
     * which is a shader DEFINE and therefore a whole extra program. Five of ours were paying
     * that for a handful of flat tints. Everything that needed only ONE colour, or a small
     * fixed set of colours, now goes through `ctx.materials.get(key, {tint})` — a cached,
     * SHARED, weather-driven variant that costs one draw call and zero programs.
     *
     * Still vertex-coloured, and deliberately: the masonry families, where every lot carries
     * its own tint inside one merged per-sector mesh. Those five share a single program.
     */
    this.mats = {
      brick: vc('building.brick'), stone: vc('building.stone'), render: vc('building.render'),
      // building.trim's base is 0x6d6457 — far too dark to carry a PALE limestone lintel,
      // and the pale lintel band is the single strongest Montreal facade cue. A second
      // tintable building.stone clone gives the value we need. See docs/report-world.md.
      trim: vc('building.stone', 'trim'), roof: vc('building.roof'),
      glass: M.get('building.glass'),
      road: M.get('road.asphalt'),
      // THE KERB LINE — see T.tints.sidewalk / T.tints.kerbFace for the measurement and the
      // REFERENCE §3.2 quote. Cached tint variants, so both still track the look (§4.9e).
      walk: M.get('road.sidewalk', { tint: T.tints.sidewalk }),
      kerb: M.get('road.curb', { tint: T.tints.kerbFace }),
      // `road.markings` already ships polygonOffset (see src/materials/library.js), so the two
      // lane-paint clones round 4 carried were buying nothing but a §9 program. Shared now.
      markW: M.get('road.markings'), markY: M.get('road.markings.yellow'),
      patch: decal('road.patch.cured'),
      /**
       * TAR SEAM. reference §7.2: "every patch has a dark tar seam at its boundary — #2A2827".
       * A cured patch is `#8C8C8C`, indistinguishable in value from the road, so WITHOUT the
       * seam the quilt is invisible and `pothole-fill` criterion 7 cannot pass. One cached
       * tint variant of the same key: one draw call for the whole city, zero shader programs.
       */
      patchSeam: M.get('road.patch.cured', { tint: T.tints.patchSeam }),
      patchMid: M.get('road.patch.cured', { tint: T.tints.patchMid }),
      // `ground.grass` not `prop.tree`: the tree material is an alpha-CUTOUT, and a cutout on
      // a plain (non-instanced) mesh is a whole extra shader program (§9, measured 49/45) for
      // four solid ground meshes that have nothing to cut out. It also carries a snow map, so
      // the outer ground and the mountain now go white in winter instead of staying green.
      outer: M.get('ground.grass', { tint: T.tints.outerGround }),
      hill: T.tints.mountain.map((t) => M.get('ground.grass', { tint: t })),
      /**
       * The Croix du Mont-Royal. Was `road.markings` tinted `crossWhite` — a TRANSPARENT,
       * depthWrite-off lane-paint material carrying a solid 26 m steel object, at a value that
       * fog washed to exactly the sky's. Opaque masonry at steel grey silhouettes instead.
       */
      white: M.get('building.stone', { tint: T.tints.crossSteel }),
      snowCap: M.get('prop.snowbank'),
    };

    /* --- procedural signage atlas ----------------------------------------
     *
     * The atlas OUTLIVES a rebuild. Its layout is a pure function of the tuning table, so the
     * texture object and its canvas are allocated once and redrawn in place on reset(); only
     * the rng-driven weathering changes. Two reasons, both load-bearing:
     *
     *   1. The material variants below are cached by `key|m<texture name>` (library.js
     *      `variantKey`). If reset() minted a NEW CanvasTexture with the same name the cache
     *      would hand back a material still pointing at the DISPOSED old one; if it minted a
     *      unique name it would leak one material per reset into the library cache.
     *   2. §7.2b — a reset that leaks shows up as heapGrowthMB. A 2048² canvas is 16 MB of
     *      backing store; not re-allocating it per reset is the cheapest heap win world has.
     */
    if (!this.atlas) {
      this.atlas = new SignAtlas(T, rngSign);
      this.atlas.build();
      this._atlasTex = this.atlas.texture;
    } else {
      this.atlas.redraw(rngSign);
    }
    const tex = this._atlasTex;
    ctx.assets.register(Promise.resolve(tex));

    /**
     * §4.9e. `alphaTest` and `emissiveMap` are the two properties this signage needs that the
     * `get()` opts bag cannot express. Applying them once, here, to the cached variant is safe
     * in a way that mutating a bare `get(key)` would not be:
     *
     *   · the variant is keyed on the ATLAS TEXTURE, which is world's and unique to world, so
     *     no other subsystem can ever name — let alone receive — this material;
     *   · `MaterialLibrary._applyToOne` touches roughness, colour, emissiveIntensity,
     *     envMapIntensity and (for snow keys) `map`. It never touches alphaTest or emissiveMap,
     *     so the two writes below survive every look update instead of fighting one.
     *
     * Everything that IS look-owned — the night emissive ramp, the weather albedo grade, the
     * wet roughness — now arrives on its own, which is the whole point of the change.
     */
    const signMat = M.get('sign.panel', { map: tex, tint: 0xffffff, side: THREE.FrontSide });
    this._adoptSign(signMat, T.signs.panelAlphaTest, null, 'world:signs');

    const litMat = M.get('sign.emissive', { map: tex, tint: 0xffffff, side: THREE.FrontSide });
    this._adoptSign(litMat, T.signs.litAlphaTest, tex, 'world:signs.lit');

    // --- ground / streets -------------------------------------------------
    const gb = {
      road: new MeshBuilder(false),
      walk: new MeshBuilder(false),
      kerb: new MeshBuilder(false),
      markW: new MeshBuilder(false),
      markY: new MeshBuilder(false),
      patch: new MeshBuilder(false),
      patchSeam: new MeshBuilder(false),
      patchMid: new MeshBuilder(false),
      outer: new MeshBuilder(false),
    };
    const skyTiers = T.landmarks.skyline.tierZ.map(() => ({
      wall: new MeshBuilder(false), band: new MeshBuilder(false),
    }));
    this.graph.buildSurfaces(gb);
    this.graph.buildKerbs(gb.kerb);
    this.graph.buildMarkings({ white: gb.markW, yellow: gb.markY });
    /**
     * Spawn points are built HERE, ahead of the quilt, so `buildPatches` can seed a family of
     * cured patches around every pothole site (see graph.buildPatches). `fork(label)` is
     * derived from (seed, label) and is order-independent (§6), so moving this call does not
     * perturb one prop, one lot or one patch that was already placed.
     */
    this._buildSpawns(rngGeo.fork('spawns'));
    this.graph.buildPatches(gb.patch, rngGeo.fork('patches'), gb.patchSeam, gb.patchMid,
      this._spawns.pothole);
    buildOuterGround(T, this.graph, gb.outer);
    buildSkyline(T, this.graph, skyTiers, rngGeo.fork('skyline'));

    this.ground = new THREE.Group();
    this.ground.name = 'world:ground';
    this.root.add(this.ground);

    // --- block layout -----------------------------------------------------
    this.blocks = layoutBlocks(this.graph, T, rngGeo.fork('layout'));
    /**
     * Block interiors. City blocks get the paved yard on the sidewalk material; PARK blocks get
     * grass instead — routing them to the same builder made the whole of Mont Royal a white
     * plaza, which was the first thing wrong with the belvédère shot. `gb.outer` is the
     * `ground.grass` variant the horizon skirt already uses, so this costs no extra draw call
     * and it takes the winter snow map with it.
     */
    for (const b of this.blocks) {
      this.graph.buildYard(b.isPark ? gb.outer : gb.walk, b.x0, b.z0, b.x1, b.z1);
    }

    this._addMesh(this.ground, gb.road, this.mats.road, 'roadway', 1);
    this._addMesh(this.ground, gb.walk, this.mats.walk, 'sidewalk', 1);
    this._addMesh(this.ground, gb.kerb, this.mats.kerb, 'kerbs', 1);
    this._addMesh(this.ground, gb.patchSeam, this.mats.patchSeam, 'patchSeams', 1);
    this._addMesh(this.ground, gb.patch, this.mats.patch, 'patches', 1);
    this._addMesh(this.ground, gb.patchMid, this.mats.patchMid, 'patchesMid', 1);
    this._addMesh(this.ground, gb.markW, this.mats.markW, 'markings', 1);
    this._addMesh(this.ground, gb.markY, this.mats.markY, 'markings.yellow', 1);
    this._addMesh(this.ground, gb.outer, this.mats.outer, 'outerGround', 1);
    const SK = T.landmarks.skyline;
    for (let i = 0; i < skyTiers.length; i++) {
      this._addMesh(this.ground, skyTiers[i].wall, M.get('building.stone', { tint: SK.tierTint[i] }), 'skyline' + i, 0);
      this._addMesh(this.ground, skyTiers[i].band, M.get('building.stone', { tint: SK.bandTint[i] }), 'skyband' + i, 0);
    }

    // --- sector builders --------------------------------------------------
    this.sectors = [];
    /**
     * ROUND-7 DRAW-CALL SURGERY. §9 caps draws at 320; `winter-slush` measured 343.
     *
     * The nine sector groups were emitting SEVEN merged meshes each — 63 draws — and the
     * measured split was wildly lopsided: `sector*.trim` carried 6,300-9,300 triangles apiece
     * while `brick`, `stone`, `render`, `roof` and `glass` carried 80-695 EACH. Forty-five
     * draw calls, plus their shadow-map twins, for about 12,000 triangles in total across the
     * whole city.
     *
     * Those five families are now built into ONE global builder each and finalised once. They
     * lose per-sector distance culling, which at 12k triangles is worth nothing, and the two
     * families that actually carry geometry — `trim` and the winter cornice `snow` — keep
     * their per-sector split so streaming still does its job where it pays.
     */
    const globalFam = {
      brick: new MeshBuilder(true), stone: new MeshBuilder(true), render: new MeshBuilder(true),
      roof: new MeshBuilder(true), glass: new MeshBuilder(false),
      /**
       * Cornice snow is GLOBAL, not per-sector.
       *
       * It used to be one mesh per sector so it streamed with the masonry it sits on. Raising
       * SECTOR_DIM to 5 turned that into 25 draw calls, and `winter-slush` measured 327 against
       * the 320 cap — a §9 breach introduced purely by the sector split. Per-sector culling was
       * never worth much here anyway: this is a thin cap riding on the parapets, and the whole
       * city's worth of it is a fraction of one trim mesh. One draw call, winter only.
       */
      snow: new MeshBuilder(false),
    };
    const SECTORED = ['trim'];
    /**
     * The three curtain-wall families are SECTORED, not global, and that is the whole reason
     * the far skyline shell exists. Downtown is one contiguous corner of the map, so in
     * practice only the sector(s) that actually contain towers emit these meshes at all
     * (`_addMesh` returns null for an empty builder) — one or two sectors × three families,
     * not nine. In exchange the tower detail gets distance culling, and `world:skylineShell`
     * carries the silhouette past the cull radius. See src/world/downtown.js.
     */
    const TOWER_FAM = ['towerGlassBright', 'towerGlassDim', 'towerGlassDark',
      'towerSpandrel', 'towerPodium'];
    const sectorBuilders = [];
    for (let s = 0; s < SECTORS; s++) {
      const g = new THREE.Group();
      g.name = 'world:sector' + s;
      this.root.add(g);
      this.sectors.push({ id: s, group: g, cx: 0, cz: 0, radius: 80, meshes: [] });
      sectorBuilders.push({
        ...globalFam,
        trim: new MeshBuilder(true),
        // One builder per lit-density variant. They are uniform-only variants of one program
        // (§9 programs unchanged); the cost is one merged mesh per variant that a downtown
        // sector actually uses, and `_addMesh` emits nothing for the ones it does not.
        towerGlassBright: new MeshBuilder(false),
        towerGlassDim: new MeshBuilder(false),
        towerGlassDark: new MeshBuilder(false),
        towerSpandrel: new MeshBuilder(false),
        towerPodium: new MeshBuilder(false),
      });
    }
    // `look` owns these (N10); world only ever asks for them by key. `towerMaterials` falls
    // back to the general building set if they have not landed, rather than letting
    // MaterialLibrary hand back a quadrant of debug magenta.
    this.towerMats = towerMaterials(M, T);

    // --- instanced sets ---------------------------------------------------
    this.propGroup = new THREE.Group();
    this.propGroup.name = 'world:props';
    this.root.add(this.propGroup);

    /**
     * LEVEL OF DETAIL (§9 — round 4 shipped 1.53M triangles against a 900k budget).
     *
     * Staircases, balconies and window reveals are the three most-repeated pieces of geometry
     * in the city: 227 stairs at up to 1,100 tris, 389 balconies at up to 266, and 4,503 window
     * reveals. Together they were ~285k triangles, and because most of them also cast into the
     * shadow map they were charged twice. Each now has a coarse twin (see staircase.js and
     * `windowGeo(true)`); `LodPair` fans one instance list out to both and the streamer draws
     * exactly one of them per block. Nothing is removed — at the swap distance the two
     * silhouettes are identical.
     */
    this.stairVariants = buildStairVariants(T);
    this.balconyVariants = buildBalconyVariants(T);
    this.stairVariantsFar = buildStairVariants(T, true);
    this.balconyVariantsFar = buildBalconyVariants(T, true);
    const stairMat = M.get('staircase.metal');
    const lodSets = [];
    const pair = (name, nearGeo, farGeo, mat, color) => {
      const near = new PropSet(name, nearGeo, mat, color);
      const far = new PropSet(name + '.far', farGeo, mat, color);
      lodSets.push(near, far);
      return new LodPair(near, far);
    };
    const stairSets = this.stairVariants.map((v, i) =>
      pair('stair' + i, v.geometry, this.stairVariantsFar[i].geometry, stairMat, true));
    const balconySets = this.balconyVariants.map((v, i) =>
      pair('balcony' + i, v.geometry, this.balconyVariantsFar[i].geometry, stairMat, true));
    const windowSet = pair('window', windowGeo(false), windowGeo(true), M.get('building.window'), true);

    const signs = new MeshBuilder(false);
    // Backlit boxes: fascia boards, projecting blade signs and lit window lettering.
    const lit = new MeshBuilder(false);
    const colliders = [];
    const landmarks = [];
    /** One record per tower shaft, for the always-resident far skyline (see downtown.js). */
    const shells = [];

    // --- buildings --------------------------------------------------------
    const lb = new LotBuilder(T, this.graph, rngGeo.fork('lots'), {
      sectorBuilders, windows: windowSet, stairs: stairSets, balconies: balconySets,
      signs, lit, colliders, landmarks, atlas: this.atlas, shells,
      stairVariants: this.stairVariants, balconyVariants: this.balconyVariants,
    });
    for (const block of this.blocks) {
      for (const lot of block.lots) lb.build(lot, block);
    }
    this.lotStats = lb.stats;

    // --- landmarks --------------------------------------------------------
    const lmGroup = new THREE.Group();
    lmGroup.name = 'world:landmarks';
    this.root.add(lmGroup);
    const cb = {
      stone: new MeshBuilder(true), roof: new MeshBuilder(true),
      trim: new MeshBuilder(true), glass: new MeshBuilder(false),
      hills: T.tints.mountain.map(() => new MeshBuilder(false)),
      white: new MeshBuilder(false),
    };
    landmarks.push(buildChurch(T, this.graph, cb));
    landmarks.push(buildMountain(T, this.graph, cb));
    for (const r of this.graph.ruelles) {
      landmarks.push({
        name: r.name, kind: 'ruelle',
        x: r.axis === 'ns' ? r.pos : (r.from + r.to) * 0.5,
        z: r.axis === 'ns' ? (r.from + r.to) * 0.5 : r.pos,
      });
    }
    this._addMesh(lmGroup, cb.stone, this.mats.stone, 'church.stone', 2);
    this._addMesh(lmGroup, cb.roof, this.mats.roof, 'church.roof', 2);
    this._addMesh(lmGroup, cb.trim, this.mats.trim, 'church.trim', 2);
    this._addMesh(lmGroup, cb.glass, this.mats.glass, 'church.glass', 1);
    for (let i = 0; i < cb.hills.length; i++) {
      this._addMesh(lmGroup, cb.hills[i], this.mats.hill[i], 'montroyal' + i, 0);
    }
    this._addMesh(lmGroup, cb.white, this.mats.white, 'croix', 0);

    const C = T.landmarks.church;
    colliders.push({
      minX: C.x - C.naveW * 0.6, minZ: C.z - C.naveD * 0.55,
      maxX: C.x + C.naveW * 0.6, maxZ: C.z + C.naveD * 0.75, top: 40, kind: 'building',
    });

    // --- props ------------------------------------------------------------
    this.props = new Props(ctx, T, this.graph, rngProp, this.atlas);
    // Park blocks, so street furniture that belongs to a city street stays off the mountain.
    // Downtown blocks (score past the midrise transition) additionally go wire-free — see
    // `noWireBlocks` in props.js for the reasoning.
    for (const b of this.blocks) {
      if (b.isPark) this.props.parkBlocks.add(b.id);
      if ((b.downtown || 0) >= 0.10) this.props.noWireBlocks.add(b.id);
    }
    this.props.signBuilder = signs;
    this.props.collidersOut = colliders;
    this.props.createSets();
    this.props.placeStreets();
    this.props.placeIntersections();
    this.props.placeBlocks(this.blocks);
    // Runs last, on its own rng fork, so the round-4 city is bit-for-bit unchanged underneath it.
    this.props.placeFurniture();
    // Lane-closure cone corridors down the middle of the carriageway. Own rng fork, so it
    // adds to the existing city without shifting a single prop already placed.
    this.props.placeConeCorridors();
    /**
     * THE EDGE OF THE PLAYFIELD (src/world/boundary.js).
     *
     * The street grid is finite and, until this landed, unmarked and unfenced: the truck drove
     * straight off the outermost sidewalk into a hole in the ground skirt. This emits the
     * concrete perimeter, the four half-space colliders that stop the truck reaching it at
     * speed, and the orange works barricades against it.
     *
     * Placed HERE, between placeConeCorridors() and finalize(), because it needs the
     * `barricade` PropSet to exist (createSets) and the instance buffers not yet baked
     * (finalize). Its own rng fork, so nothing already placed in the city moves (§6).
     *
     * It is deliberately NOT block-streamed. `blockAt()` clamps out-of-range coordinates onto
     * the nearest edge block, so a perimeter run assigned that way would stream OUT whenever
     * the player was more than `stream.playerRadius` from that block's centre — which is most
     * of the time, and is precisely the case where a distant edge is on screen. One merged
     * mesh that is always resident is both correct and cheaper than the alternative. The
     * barricades DO stream, through the ordinary props path, because they are decoration.
     */
    /**
     * MONT ROYAL. Forest on every park block, then the belvédère on the summit. Both run on
     * their own position-keyed forks so nothing already placed in the city moves (§6), and
     * both go in before `props.finalize()` because they add to the existing instanced sets.
     */
    const rngPark = rngGeo.fork('park');
    this.parkStats = { blocks: 0, trees: 0 };
    for (const b of this.blocks) {
      if (!b.isPark) continue;
      this.parkStats.blocks++;
      this.parkStats.trees += plantForest(T, this.graph, b, rngPark.fork(posKey('forest', b.cx, b.cz)), this.props);
    }
    const lookoutDeck = new MeshBuilder(false);
    const lookoutWall = new MeshBuilder(false);
    this.lookout = buildLookout(T, this.graph, lookoutDeck, lookoutWall, colliders, this.props, rngPark);

    const boundaryMb = new MeshBuilder(false);
    this.boundaryStats = buildBoundary(T, this.graph, boundaryMb, colliders, this.props,
      rngGeo.fork('boundary'));
    this.props.finalize(this.propGroup);
    // Receive-only: the barrier's own cast shadow would fall on the void outside the map, and
    // a caster is charged twice against §9 (see the SHADOW BUDGET note below).
    this._addMesh(this.ground, boundaryMb,
      M.get('road.curb', { tint: T.tints.boundaryConcrete }), 'boundary', 1);

    // The belvédère. Paving on the sidewalk material, parapet on the kerb concrete — both are
    // cached tint variants that already exist, so this is two draw calls and no new programs.
    this._addMesh(this.ground, lookoutDeck, this.mats.walk, 'lookout.deck', 1);
    this._addMesh(this.ground, lookoutWall,
      M.get('road.curb', { tint: T.tints.boundaryConcrete }), 'lookout.parapet', 2);

    /**
     * THE FAR SKYLINE SHELL. One open prism per tower shaft, always resident, never streamed
     * and never sector-culled — the same treatment as the perimeter barrier above, and for the
     * same reason: a 130 m silhouette that blinks out when its sector group switches off is
     * the most visible pop the game could have. Each shell is inset inside the detailed shaft
     * that stands in the same place, so where both are drawn it contributes no pixels. See
     * src/world/downtown.js.
     */
    /**
     * THE VISTA LAYER. Built here, after every lot has recorded its `builtTop`. Grouped by
     * sector and swapped against sector detail in update(), exactly like the downtown shells.
     */
    this._shellMeshes = [];
    const vistaMbs = new Map();
    this.vistaCount = buildVistaShells(T, this.graph, this.blocks, (sec) => {
      if (!vistaMbs.has(sec)) vistaMbs.set(sec, new MeshBuilder(false));
      return vistaMbs.get(sec);
    });
    const vistaMat = M.get('building.stone', { tint: T.tints.vistaShell });
    for (const [sec, mb] of vistaMbs) {
      const mesh = this._addMesh(this.ground, mb, vistaMat, 'vistaShell' + sec, 0);
      if (mesh) this._shellMeshes.push({ mesh, sector: this.sectors[sec], vista: true });
    }

    const shellMbs = new Map();
    buildSkylineShells(T, shells, (sec) => {
      if (!shellMbs.has(sec)) shellMbs.set(sec, new MeshBuilder(false));
      return shellMbs.get(sec);
    });
    this.shellCount = shells.length;
    for (const [sec, mb] of shellMbs) {
      const mesh = this._addMesh(this.ground, mb, this.towerMats.spandrel, 'skylineShell' + sec, 0);
      if (mesh) this._shellMeshes.push({ mesh, sector: this.sectors[sec] });
    }
    /**
     * ⚠ THE OVERHEAD WIRES WERE ON A CHAIN-LINK CUTOUT MATERIAL.
     *
     * `wide-establish`, every round since 8: *"No overhead wires anywhere. The overhead wire
     * tangle is the defining silhouette of a Plateau street; A has it in three places, B has
     * none."* And the census says `world:wires` is 8,544 live triangles — 178 spans of it —
     * so the geometry has been in the frame the whole time.
     *
     * `prop.fence` is `alphaTest: 0.42` over `TextureLab.fence`, which is a CHAIN-LINK card:
     * alpha 1 on the strands, 0 in the ~90 % of the cell between them. Every wire quad was
     * sampling that card across its whole length, so 90 % of every wire was discarded by the
     * alpha test and what survived was a dashed line one pixel wide. Opaque dark metal
     * instead — a cached tint variant, so one draw call and zero shader programs (§9).
     */
    this._addMesh(this.propGroup, this.props.wires,
      M.get('prop.metal', { tint: T.props.wireTint }), 'wires', 0);

    // Winter dress: bare limbs + snow load on the trees, ploughed kerb banks, snow caps on
    // every horizontal surface. Exactly one of the two crowns is ever visible.
    this._winterSets = Props.WINTER_SETS.flatMap((n) => this.props.meshesFor(n));
    this._summerSets = this.props.summerSets().flatMap((n) => this.props.meshesFor(n));

    // --- sector meshes ----------------------------------------------------
    const famMat = {
      brick: this.mats.brick, stone: this.mats.stone, render: this.mats.render,
      trim: this.mats.trim, roof: this.mats.roof, glass: this.mats.glass,
    };
    // The five light families, once, for the whole city. See the block comment at globalFam.
    for (const key of ['brick', 'stone', 'render', 'roof', 'glass']) {
      /**
       * ROUND-11. `masonry.roof` is 10.9k triangles of mansard planes, dormers, vent stacks and
       * the flat roof deck, and casting it charged all of that twice for a shadow that falls on
       * ROOFS — surfaces no graded camera can see under. The three wall families keep casting:
       * they are the mass that throws the band across the street.
       */
      this._addMesh(this.ground, globalFam[key], famMat[key], 'masonry.' + key, key === 'roof' ? 1 : 2);
    }
    for (let s = 0; s < SECTORS; s++) {
      const sec = this.sectors[s];
      sec.casters = [];
      sec.casting = true;
      for (const key of SECTORED) {
        const mesh = this._addMesh(sec.group, sectorBuilders[s][key], famMat[key], 'sector' + s + '.' + key, 2);
        if (mesh) { sec.meshes.push(mesh); sec.casters.push(mesh); }
      }
      /**
       * Curtain wall. Only the sector(s) that actually contain downtown emit these at all —
       * `_addMesh` returns null for an empty builder — so the expansion costs about three
       * draw calls, not twenty-seven. The podium CASTS (it is the mass at street level that
       * throws a shadow you can stand in); the shaft and its fins do not, because a tower's
       * own shadow at this scale lands on blocks that are themselves streamed out, and a
       * caster is charged twice against the §9 triangle counter.
       */
      for (const key of TOWER_FAM) {
        const tm = key === 'towerSpandrel' ? this.towerMats.spandrel
          : (key === 'towerPodium' ? this.towerMats.podium
            : this.towerMats[key.slice('towerGlass'.length)]);
        const mesh = this._addMesh(sec.group, sectorBuilders[s][key], tm,
          'sector' + s + '.' + key, key === 'towerPodium' ? 2 : 1);
        if (mesh) {
          sec.meshes.push(mesh);
          if (key === 'towerPodium') sec.casters.push(mesh);
        }
      }
    }
    /**
     * SECTOR BOUNDS, MEASURED FROM THE BLOCKS THEMSELVES.
     *
     * This used to index two corner blocks arithmetically — `blocks[(si*2)*nJ + (sj*2)]` — which
     * silently assumed a 6×6 block field, i.e. exactly two blocks per sector. The phase-1
     * expansion makes it 9×9, and that expression would have read the wrong blocks and given
     * every sector a wrong centre and radius, so distance culling would have hidden masonry
     * that was on screen. `sectorOf()` (buildings.js) now defines the partition and this is a
     * plain min/max over whatever it produced, which is correct for any grid size.
     *
     * SECTORS stays at 9 deliberately. It is a 3×3 ARRANGEMENT, not a fixed 2×2 block size, so
     * growing the city makes each sector bigger rather than making more of them — and the draw
     * call count (§9, already tight) is unchanged by the expansion.
     */
    const snowMesh = this._addMesh(this.ground, globalFam.snow, this.mats.snowCap, 'corniceSnow', 1);
    if (snowMesh) this._corniceSnow.push(snowMesh);

    for (const sec of this.sectors) { sec.minX = Infinity; sec.minZ = Infinity; sec.maxX = -Infinity; sec.maxZ = -Infinity; }
    for (const b of this.blocks) {
      const sec = this.sectors[b.sector];
      if (b.x0 < sec.minX) sec.minX = b.x0;
      if (b.z0 < sec.minZ) sec.minZ = b.z0;
      if (b.x1 > sec.maxX) sec.maxX = b.x1;
      if (b.z1 > sec.maxZ) sec.maxZ = b.z1;
    }
    for (const sec of this.sectors) {
      if (!Number.isFinite(sec.minX)) { sec.cx = 0; sec.cz = 0; sec.radius = 0; continue; }
      sec.cx = (sec.minX + sec.maxX) * 0.5;
      sec.cz = (sec.minZ + sec.maxZ) * 0.5;
      sec.radius = Math.max(sec.maxX - sec.minX, sec.maxZ - sec.minZ) * 0.72;
    }

    // --- signage ----------------------------------------------------------
    this._addMesh(this.root, signs, signMat, 'signs', 1);
    this._addMesh(this.root, lit, litMat, 'signs.lit', 1);

    // --- instanced meshes into the scene ----------------------------------
    this.instSets = [];
    for (const set of lodSets) {
      const mesh = set.finalize();
      if (mesh) { this.propGroup.add(mesh); this.instSets.push(set); }
    }
    for (const s of this.props.all) if (s.mesh) this.instSets.push(s);
    // A/B switch for the instance-upload skip (see PropSet.stream) — tuning-driven so the
    // measurement can be repeated without editing code.
    for (const s of this.instSets) s.uploadSkip = T.stream.instanceUploadSkip;
    // Thin, flat or self-lit instances are not worth a shadow-map draw.
    const NO_CAST = new Set(['window', 'globeO', 'globeC', 'signalLens', 'laundry',
      'fenceC', 'snowDirt', 'binSnow', 'coneSnow', 'barricadeSnow', 'branchSnow',
      'carSnow0', 'carSnow1', 'carSnow2', 'contact',
      /**
       * ROUND-5. These now carry a contact blob, and a shadow-map pass for each of them was
       * costing a draw call and a second copy of its triangles for a shadow you cannot pick
       * out from the object's own dark side. The props the critics named specifically —
       * poles, lampposts, sign posts, street trees, cones, staircases, vehicles, buildings —
       * all still cast.
       */
      'bin', 'hydrant', 'rack', 'bixi', 'hedge', 'planter', 'fenceW',
      /**
       * ROUND-11 §9. `winter-slush` gates at 956,651 and `tools/tribudget.mjs` decomposes
       * 193,214 of that — 20.2 % — as the shadow-map pass re-rendering casters. Each name
       * below is a caster whose shadow is already inside another object's, or is too small to
       * resolve, and dropping it costs a draw call and a second copy of its triangles with no
       * visible change:
       *
       *   snow / branch  a ploughed bank's shadow falls in the gutter it is standing in;
       *                  a bare limb's is one pixel of noise against a snow-lit street
       *   trunk          entirely inside the canopy's own shadow, which still casts
       *   bench/skip/…   sub-metre street furniture that already carries a contact blob
       *   post/barricade same, and both are dark objects on a bright road
       */
      'snow', 'branch', 'trunk', 'bench', 'benchIron', 'skip', 'garage', 'post', 'barricade',
      /**
       * ROUND-7. `carTrim0/1/2` measured 46,240 triangles live across the graded shots and
       * every one of them was charged TWICE, for a shadow that lands entirely inside the
       * shadow the car BODY is already casting one centimetre outboard of it. The body still
       * casts; the tyres, valance and rubbing strips do not.
       */
      'carTrim0', 'carTrim1', 'carTrim2', 'carGlass0', 'carGlass1', 'carGlass2',
      // A rim's shadow falls inside the tyre's; a tail lamp is a 0.30 × 0.13 m quad.
      'carRim0', 'carRim1', 'carRim2', 'carLamp0', 'carLamp1', 'carLamp2']);
    /**
     * SHADOW BUDGET. `renderer.info.render.triangles` counts the shadow-map pass too, so every
     * casting instance is charged twice. The far LOD sets stop casting entirely: at >74 m a
     * prop's own shadow is a couple of pixels, and the contact blob under it is now carrying
     * the grounding read that the shadow map used to be responsible for.
     */
    for (const s of this.instSets) {
      if (!s.mesh) continue;
      if (NO_CAST.has(s.name) || s.name.endsWith('.far')) s.mesh.castShadow = false;
    }
    this._applySeason();

    // --- collision --------------------------------------------------------
    this.collision = new StaticCollision(this.graph.bounds, T.collision.cell, T.collision.maxContacts);
    for (const c of colliders) {
      if (c.r != null) this.collision.addCircle(c.x, c.z, c.r, c.y0 != null ? c.y0 : -1, c.top, c.kind);
      else this.collision.addBox(c.minX, c.minZ, c.maxX, c.maxZ, c.y0 != null ? c.y0 : -1, c.top, c.kind);
    }
    this.collision.bake();

    // --- landmark index ---------------------------------------------------
    // (spawn points are built earlier — see the note by buildPatches)
    this.landmarks = landmarks;

    // --- streaming state ---------------------------------------------------
    this.active = new Uint8Array(this.blocks.length);
    this.near = new Uint8Array(this.blocks.length);
    this._activeCount = 0;
    this._nearCount = 0;

    // --- bootstrap lighting (removes itself the moment `look` lands) --------
    this._installBootstrapLighting();

    this._warmFrames = this.T.stream.warmFrames;
    this._built = true;
    /**
     * READ-ONLY DEBUG HANDLE. `window.PPM` exposes only the harness API, so there is no way
     * from a Playwright probe to reach the scene graph and ask "is this instanced set actually
     * drawing, and where are its matrices?". That question is what found the round-6 contact
     * shadow bug: the blobs existed, `harnessStats().contactShadows` was 3,666, and they were
     * still contributing 0.34 % of the frame. Not part of any published interface (§4), not
     * read by any subsystem, and it does not touch `window.PPM`.
     */
    if (typeof window !== 'undefined') window.__WORLD_DEBUG = this;
  }

  /**
   * TEMPORARY BRIDGE. `look` (ARCHITECTURE.md N10) owns lighting, sky, fog and tonemapping.
   * Until that subsystem lands there is no light in the scene at all and every shot captures
   * black, so `world` installs a minimal key/fill/rim rig plus a flat sky colour and DOES NOT
   * install it once `look` reports it is no longer a stub. Nothing else in src/world/ touches
   * renderer state. Delete this method when `look` ships.
   */
  _installBootstrapLighting() {
    const look = this.ctx.sys ? this.ctx.sys('look') : null;
    const lookLive = look && !look.isNull && !look.stub;
    if (lookLive) return;

    const g = new THREE.Group();
    g.name = 'world:bootstrapLighting';
    const hemi = new THREE.HemisphereLight(0xa8c9f2, 0x6d6450, 1.05);
    const sun = new THREE.DirectionalLight(0xfff0d4, 2.4);
    sun.position.set(-130, 155, 100);
    sun.target.position.set(0, 0, -20);
    const rim = new THREE.DirectionalLight(0xbcd6ff, 0.5);
    rim.position.set(150, 65, -170);
    g.add(hemi, sun, sun.target, rim);
    this.ctx.scene.add(g);
    this._bootstrapLights = g;

    if (!this.ctx.scene.background) {
      this.ctx.scene.background = new THREE.Color(0x4d86ee);
      this._bootstrapSky = true;
    }
  }

  /**
   * Apply the two signage properties `MaterialLibrary.get()` cannot express, exactly once.
   * Idempotent, so a rebuild that hits the variant cache is a no-op. See the block comment at
   * the call site for why writing to a cached variant is safe here and only here.
   */
  _adoptSign(m, alphaTest, emissiveMap, name) {
    let dirty = false;
    // Belt and braces: the variant is cached on the atlas texture's NAME, so a full
    // dispose()/re-create cycle (rather than reset(), which reuses the atlas) could hand back
    // a material still bound to the old texture object. Re-point it rather than trust it.
    if (this._atlasTex && m.map !== this._atlasTex) { m.map = this._atlasTex; dirty = true; }
    if (m.alphaTest !== alphaTest) { m.alphaTest = alphaTest; dirty = true; }
    if (m.transparent) { m.transparent = false; dirty = true; }
    if (emissiveMap && m.emissiveMap !== emissiveMap) { m.emissiveMap = emissiveMap; dirty = true; }
    if (dirty) m.needsUpdate = true;
    m.name = name;
    return m;
  }

  /** shadow: 2 = cast + receive, 1 = receive only, 0 = neither. Flat ground never casts. */
  _addMesh(parent, mb, material, name, shadow) {
    if (!mb || mb.isEmpty) return null;
    const geo = mb.toGeometry(name);
    this._geoms.push(geo);
    const mesh = new THREE.Mesh(geo, material);
    mesh.name = 'world:' + name;
    mesh.castShadow = shadow >= 2;
    mesh.receiveShadow = shadow >= 1;
    parent.add(mesh);
    return mesh;
  }

  // ================================================================== spawns

  _buildSpawns(rng) {
    const g = this.graph, S = this.T.spawn;
    const pothole = [], traffic = [], ped = [], cone = [];

    const push = (arr, x, z, y, dx, dz, axis = -1, streetIndex = -1) => {
      arr.push({
        pos: new THREE.Vector3(x, y, z), dir: { x: dx, y: dz }, axis, streetIndex,
      });
    };

    for (let axis = 0; axis < 2; axis++) {
      const lines = axis === 0 ? g.nsX : g.ewZ;
      const halves = axis === 0 ? g.nsHalf : g.ewHalf;
      const walks = axis === 0 ? g.nsWalk : g.ewWalk;
      const a0 = axis === 0 ? g.corridorMinZ : g.corridorMinX;
      const a1 = axis === 0 ? g.corridorMaxZ : g.corridorMaxX;
      const at = (along, lat, base) => (axis === 0 ? [base + lat, along] : [along, base + lat]);
      const near = (x, z) => (axis === 0
        ? Math.abs(z - g.ewZ[g._ewNear(z)]) < g.ewHalf[g._ewNear(z)] + S.intersectionClear
        : Math.abs(x - g.nsX[g._nsNear(x)]) < g.nsHalf[g._nsNear(x)] + S.intersectionClear);

      for (let li = 0; li < lines.length; li++) {
        const base = lines[li], half = halves[li], walk = walks[li];

        for (let a = a0 + 10; a < a1 - 10; a += S.potholeEvery) {
          const side = rng.chance(0.5) ? 1 : -1;
          const lat = side * half * rng.range(S.potholeEdgeBias * 0.5, S.potholeEdgeBias);
          const [x, z] = at(a + rng.spread(4), lat, base);
          if (near(x, z)) continue;
          const d = axis === 0 ? [0, side > 0 ? -1 : 1] : [side > 0 ? 1 : -1, 0];
          // Gameplay samples the complete pool. Preserve the street identity so it can
          // balance rues/avenues without reverse-engineering a grid line from a lane edge.
          push(pothole, x, z, g.roadY(x, z), d[0], d[1], axis, li);
        }
        for (let a = a0 + 14; a < a1 - 14; a += S.trafficEvery) {
          for (const side of [-1, 1]) {
            const [x, z] = at(a, side * half * 0.45, base);
            if (near(x, z)) continue;
            const d = axis === 0 ? [0, side > 0 ? -1 : 1] : [side > 0 ? 1 : -1, 0];
            push(traffic, x, z, g.roadY(x, z), d[0], d[1]);
          }
        }
        for (let a = a0 + 6; a < a1 - 6; a += S.pedEvery) {
          for (const side of [-1, 1]) {
            const [x, z] = at(a, side * (half + (walk - half) * 0.55), base);
            const d = axis === 0 ? [0, 1] : [1, 0];
            push(ped, x, z, g.walkY(x, z), d[0], d[1]);
          }
        }
        for (let a = a0 + 9; a < a1 - 9; a += S.coneEvery) {
          for (const side of [-1, 1]) {
            const [x, z] = at(a + rng.spread(2), side * (half - rng.range(0.5, 2.2)), base);
            if (near(x, z)) continue;
            const d = axis === 0 ? [0, side > 0 ? -1 : 1] : [side > 0 ? 1 : -1, 0];
            push(cone, x, z, g.roadY(x, z), d[0], d[1]);
          }
        }
      }
    }
    this._spawns = { pothole, traffic, ped, cone };
  }

  // ================================================================== streaming

  fixedUpdate(dt) {
    void dt;
    if (!this._built) return;
    this._streamTick++;
    if (this._streamTick % this.T.stream.everySteps !== 0) return;
    this._restream(false);
  }

  _restream(force) {
    const S = this.T.stream;
    const p = this.ctx.player;
    const cam = this.ctx.camera;
    const px = p.position.x, pz = p.position.z;
    const cx = cam.position.x, cz = cam.position.z;
    /**
     * ⚠ `establish` USED TO MEAN "STREAM IN THE ENTIRE CITY", AND THAT DOES NOT SURVIVE A
     * BIGGER CITY.
     *
     * `_forceAll` switched every block on so an establishing shot could not show a hole. At
     * 36 blocks that was affordable. At 81 it is not: measured on `wide-establish`, world's
     * share went 506,376 → 1,011,543 triangles, and the census named the mechanism outright —
     * `cone.far` at 1,836 live instances became 4,239, `balcony2.far` 218 → 490. The shot was
     * paying full price for props in a downtown 570 m behind the camera.
     *
     * It is a RADIUS SCALE now, not a switch. `establishScale` still reaches far past the
     * ordinary streaming radius, so nothing within sight of an establishing camera is missing,
     * but the far corners of a city that is now 612 m across stop being drawn. Anything it
     * drops is beyond 400 m, where `look`'s 620 m fog plane has already taken ~75 % of it.
     */
    const scale = this._streamScale;
    const pr2 = S.playerRadius * scale * S.playerRadius * scale;
    const cr2 = S.cameraRadius * scale * S.cameraRadius * scale;

    let changed = force;
    let count = 0;
    for (let i = 0; i < this.blocks.length; i++) {
      const b = this.blocks[i];
      let on = 0;
      {
        const dx = b.cx - px, dz = b.cz - pz;
        if (dx * dx + dz * dz <= pr2) on = 1;
        else {
          const ex = b.cx - cx, ez = b.cz - cz;
          if (ex * ex + ez * ez <= cr2) on = 1;
        }
      }
      if (on !== this.active[i]) {
        changed = true;
        this.ctx.bus.emit('world:blockStreamed', { blockId: i, action: on ? 'in' : 'out' });
      }
      this.active[i] = on;
      count += on;
    }
    this._activeCount = count;
    const lodMoved = this._computeNear(true);
    if (changed) {
      this._repack(false);
      this._lastRepack = this._streamTick;
      this._lodPending = false;
      return;
    }
    if (!lodMoved && !this._lodPending) return;
    // LOD-only: rate-limited, and the ALWAYS sets are provably unchanged so they are skipped.
    if (this._streamTick - this._lastRepack < this.T.lod.minRepackSteps) {
      this._lodPending = true;
      return;
    }
    this._lodPending = false;
    this._lastRepack = this._streamTick;
    this._repack(true);
  }

  /**
   * Recompute the per-block LOD flags from the CAMERA pose (not the player's — this is a
   * screen-space judgement) and report whether anything moved.
   *
   * Deterministic, so shots stay bit-reproducible under freezeClocks(): the camera pose is
   * itself a pure function of the simulation, and hysteresis makes the result path-dependent
   * only through `this.near`, which is rebuilt from scratch on reset().
   */
  _computeNear(force = false) {
    const L = this.T.lod;
    const cam = this.ctx.camera;
    const cx = cam.position.x, cz = cam.position.z;
    // The hysteresis band is 18 m wide, so a camera that has not moved a couple of metres
    // cannot have crossed it. Skipping the scan keeps `update()` allocation- and work-free on
    // the overwhelming majority of frames (N9).
    if (!force) {
      const mx = cx - this._lodCamX, mz = cz - this._lodCamZ;
      if (mx * mx + mz * mz < L.recheckMove * L.recheckMove) return false;
    }
    this._lodCamX = cx;
    this._lodCamZ = cz;
    // A block that is already near stays near out to farRadius, so a camera parked on the
    // boundary cannot make the instance buffers thrash.
    const ln2 = L.nearRadius * L.nearRadius;
    const lf2 = L.farRadius * L.farRadius;
    let changed = false, nearCount = 0;
    for (let i = 0; i < this.blocks.length; i++) {
      const b = this.blocks[i];
      const dx = b.cx - cx, dz = b.cz - cz;
      const d2 = dx * dx + dz * dz;
      const wasNear = this.near[i] === 1;
      const isNear = wasNear ? d2 <= lf2 : d2 <= ln2;
      if (isNear !== wasNear) changed = true;
      this.near[i] = isNear ? 1 : 0;
      nearCount += isNear ? 1 : 0;
    }
    this._nearCount = nearCount;
    return changed;
  }

  /** @param {boolean} lodOnly  the active-block set is unchanged; only LOD sets need re-packing. */
  _repack(lodOnly) {
    for (let i = 0; i < this.instSets.length; i++) {
      const s = this.instSets[i];
      if (lodOnly && s.lod === LOD.ALWAYS) continue;
      s.stream(this._isActive, this._isNear);
    }
    this._applySeason();
  }

  /**
   * Swap the city between its leafy and its snow-covered dress.
   *
   * `look` owns the weather (N10) and publishes `ctx.look.snowCover` for exactly this. It is
   * NOT one of the §4.10 null-object interfaces, so it can legitimately be absent in a
   * harness context where `look` was never registered; the local weather id is the fallback
   * in that one case, and nothing else in `world` branches on it.
   */
  _applySeason() {
    if (!this._winterSets) return;
    const look = this.ctx.look;
    const cover = look && look.snowCover != null
      ? look.snowCover
      : (this._weather === 'winter' ? 1 : 0);
    const winter = cover >= this.T.props.snowGate;
    if (winter === this._winterOn) return;
    this._winterOn = winter;
    for (const s of this._winterSets) if (s.mesh) s.mesh.visible = winter;
    for (const s of this._summerSets) if (s.mesh) s.mesh.visible = !winter;
    for (const m of this._corniceSnow) m.visible = winter;
    // Overcast has no key, so the directional anchor blob becomes a soft pooling. Material is
    // world-owned (Props.contactMat is an instanceOf clone), so this mutates nothing shared.
    const cm = this.props && this.props.contactMat;
    if (cm) cm.opacity = winter ? this.T.contact.winterOpacity : this.T.contact.opacity;
  }

  update(dt, alpha) {
    void dt; void alpha;
    if (!this._built) return;
    // Sector-level distance culling. A pure function of the camera pose, so shots stay
    // bit-identical under freezeClocks().
    this._applySeason();
    // LOD has to be resolved HERE as well as in the streaming tick: a harness shot may call
    // camera(pose) and then render() without stepping, and the detail set must follow the lens
    // it is actually being judged through. Presentation-only — it rewrites instance buffers
    // and touches no simulation state.
    if (this._computeNear()) { this._repack(true); this._lodPending = false; }
    const cam = this.ctx.camera;
    const r = this.T.stream.sectorRadius;
    /**
     * ROUND-11 SHADOW BUDGET. `renderer.info.render.triangles` counts the shadow-map pass, so a
     * casting mesh is charged TWICE — and the nine `sector*.trim` meshes are the heaviest thing
     * `world` owns: measured 84.6k triangles live, i.e. 169k effective, more than a fifth of the
     * whole §9 budget on its own. They carry every cornice, coping, lintel, sill, stoop, fascia
     * and awning in the city.
     *
     * Deleting them is not an option (they are most of the facade detail this round is about)
     * and neither is splitting them, which would cost nine draw calls against a 320 cap already
     * at 314. So the CAST is gated on distance instead: a cornice's own shadow band is a real
     * cue at 20 m and is a sub-pixel smudge at 120 m, while the building's overall shadow comes
     * from the masonry mass, which still casts from everywhere. A pure function of camera pose,
     * so shots stay bit-reproducible under freezeClocks().
     */
    const cr = this.T.stream.trimShadowRadius;
    /**
     * ⚠ SECTOR VISIBILITY IS A GPU EVENT, NOT A BOOLEAN. p99 REGRESSION, ROUND 13.
     *
     * Going from 9 big sectors to 25 small ones made every merged mesh cheaper to cull but made
     * the CULLING ITSELF far more frequent, and each newly-visible mesh is a first-time WebGL
     * buffer upload on the frame it appears. Measured on the drive profile: p50 1.55 ms and
     * p95 7.5 ms — both fine — against p99 18.7-20.9 ms, i.e. a pure tail of render-thread
     * spikes with no simulation component (world.fixed p95 0.01 ms).
     *
     * Three things below, in the order they matter:
     *
     *  1. WARM-UP. For the first `warmFrames` presented frames everything is visible, so every
     *     sector mesh is uploaded once, up front, instead of drip-fed into the middle of a
     *     drive. Skipped entirely when `freezeClocks()` has been called — every graded shot
     *     freezes (harness/replay.mjs), so no capture can ever observe the warm state and §7.3
     *     reproducibility is untouched.
     *  2. HYSTERESIS. A visible sector stays visible out to `sectorHysteresis` metres past the
     *     radius that turned it on, so a camera sitting on a boundary cannot flip it every
     *     frame. Same trick `_computeNear` already uses for the LOD band, and deterministic for
     *     the same reason: the state is rebuilt from scratch on reset().
     *  3. STAGGER. At most `sectorFlipsPerFrame` visibility changes are applied per frame. When
     *     the drive crests a hill and six sectors qualify at once, their uploads are spread over
     *     six frames instead of landing in one.
     */
    /**
     * ⚠ THE WARM-UP MUST NOT SHOW THE SHELLS. It did, and that re-created the exact occlusion
     * `look` filed: forcing every shell visible alongside every detailed sector put a dark
     * shell in front of a lit curtain wall for the warm frames. Caught by re-running the shell
     * A/B after adding the warm-up — survival went back to 35.7 % at the mid camera.
     *
     * Sectors only. The shells are ~110 triangles for the whole quadrant, so there is nothing
     * worth pre-uploading in them, and falling through to the LOD swap below correctly hides
     * every one of them while their sector is forced on.
     */
    /**
     * TWO-PHASE WARM-UP, and the second phase is the one phase 3 needed.
     *
     * The first version forced only the SECTOR groups visible, because forcing the shells at the
     * same time put a dark shell in front of a lit curtain wall (see the note below). That left
     * every shell mesh un-uploaded — and phase 3 added 25 vista-shell meshes on top of the 4
     * downtown ones, each of which then paid its first-time GPU upload at some random moment
     * mid-drive as its sector fell out of the cull radius. Measured on the drive profile: p50
     * barely moved (1.73 → 1.93 ms) but p99 went 10.7 → 21.0, the signature of a spike tail.
     *
     * So the two sets are warmed in SEPARATE frames. Sectors first, then shells with the
     * sectors hidden. Every merged mesh is uploaded before gameplay, and no single frame ever
     * shows a shell and its own sector's detail together, which is the invariant the whole
     * shell design rests on. Skipped entirely when `freezeClocks()` has been called, so no
     * capture can observe either phase (§7.3).
     */
    const warming = !this._frozen && this._warmFrames > 0;
    if (warming) {
      this._warmFrames--;
      const shellPhase = this._warmFrames < this.T.stream.warmShellFrames;
      for (let i = 0; i < this.sectors.length; i++) this.sectors[i].group.visible = !shellPhase;
    }
    const hys = this.T.stream.sectorHysteresis;
    let flips = this.T.stream.sectorFlipsPerFrame;
    for (let i = 0; i < this.sectors.length; i++) {
      const s = this.sectors[i];
      const dx = s.cx - cam.position.x, dz = s.cz - cam.position.z;
      const d2 = dx * dx + dz * dz;
      const scale = this._streamScale;
      // On out to `lim`; once on, stays on out to `lim + hys`.
      const lim = (r + s.radius) * scale;
      const limOff = lim + hys;
      const want = s.group.visible ? d2 <= limOff * limOff : d2 <= lim * lim;
      if (!warming && want !== s.group.visible) {
        if (flips > 0) { s.group.visible = want; flips--; }
        // else: leave it as it is and pick it up next frame — see STAGGER above.
      }
      const clim = cr + s.radius;
      const cast = d2 <= clim * clim;
      // Only on a CHANGE: the renderer caches a depth material per (object, material) pair and
      // there is no reason to hand it a fresh reason to look at that cache 60 times a second.
      if (cast !== s.casting) {
        s.casting = cast;
        for (let k = 0; k < s.casters.length; k++) s.casters[k].castShadow = cast;
      }
    }
    /**
     * DOWNTOWN LOD SWAP. Exactly one of {detailed towers, far shell} draws per sector — see the
     * note in src/world/downtown.js for the two failed inset attempts that got us here. Pure
     * function of the sector visibility computed immediately above, so shots stay bit-identical
     * under freezeClocks(), and it is a boolean write per downtown sector with no allocation.
     */
    /**
     * THE VISTA LAYER IS GATED ON CAMERA HEIGHT, and that is a performance decision with a
     * design justification rather than the other way round.
     *
     * The far-city shells exist so the belvédère has a city to overlook. At street level they
     * are pure cost: 25 more meshes flipping in and out as sectors cull, each flip a draw-list
     * change and — before the two-phase warm-up above — a first-time upload. Measured on the
     * drive profile, they were most of a p99 that had gone 10.7 → 21.0 ms.
     *
     * They are also invisible down there. `look`'s fog closes at 620 m and a street-level
     * camera has buildings in the way; the distance the shells cover is distance you cannot see
     * from the road. Above `minCamY` — which only the mountain reaches, and which is set clear
     * of `wide-establish`'s 15 m elevated camera so no graded shot changes — they switch on.
     *
     * Pure function of camera pose, so shots stay bit-reproducible under freezeClocks().
     */
    const vistaOn = cam.position.y >= this.T.vista.minCamY;
    for (let i = 0; i < this._shellMeshes.length; i++) {
      const e = this._shellMeshes[i];
      const want = !e.sector.group.visible && (!e.vista || vistaOn);
      if (e.mesh.visible !== want) e.mesh.visible = want;
    }
  }

  onResize() {}

  // ================================================================== WorldQuery

  _makeQuery() {
    const g = this.graph;
    const self = this;
    const rayResult = { hit: false, point: new THREE.Vector3(), normal: new THREE.Vector3(), kind: 'building', distance: 0 };

    return {
      isFallback: false,
      bounds: g.bounds,

      sampleGround(x, z) { return g.sampleGround(x, z); },
      roadAt(x, z) { return g.roadAt(x, z); },
      nearestRoadPoint(x, z) { return g.nearestRoadPoint(x, z); },
      segment(id) { return g.segment(id); },
      segments() { return g.segments(); },

      spawnPoints(kind) { return self._spawns[kind] || EMPTY; },

      collide(sphere) { return self.collision.collide(sphere); },

      raycastStatic(origin, dir, max) {
        const r = self.collision.raycast(
          origin.x, origin.y, origin.z, dir.x, dir.y, dir.z,
          max != null ? max : 200
        );
        if (!r) return null;
        rayResult.hit = true;
        rayResult.point.copy(r.point);
        rayResult.normal.copy(r.normal);
        rayResult.kind = r.kind;
        rayResult.distance = r.distance;
        return rayResult;
      },

      landmarkNear(x, z) {
        let best = null, bd = Infinity;
        const list = self.landmarks;
        for (let i = 0; i < list.length; i++) {
          const l = list[i];
          const dx = l.x - x, dz = l.z - z;
          const d = Math.sqrt(dx * dx + dz * dz);
          if (d < bd) { bd = d; best = l; }
        }
        // Not pooled: the HUD holds this on a destination card for seconds at a time, and it
        // is called on target change, not per frame.
        if (!best || bd > 46) return { name: g.streetNameAt(x, z), kind: 'street', dist: 0 };
        return { name: best.name, kind: best.kind, dist: bd };
      },

      // ---- world-specific extras (not part of §4.5; safe to ignore) ----
      streetName(x, z) { return g.streetNameAt(x, z); },
      blocks() { return self.blocks; },
      activeBlocks() { return self._activeCount; },
      ruelles() { return g.ruelles; },
      landmarks() { return self.landmarks; },
    };
  }

  // ================================================================== harness

  applyHarnessState(patch) {
    if (!this._built) return;
    if (patch.weather) {
      this._weather = patch.weather;
      this._applySeason();
    }
    if (patch.scenario !== undefined) {
      // The establishing / result shots must show the whole block; nothing streams out.
      this._streamScale = ESTABLISH_SCENARIOS.has(patch.scenario) ? this.T.stream.establishScale : 1;
      this._restream(true);
    }
  }

  harnessStats() {
    if (!this._built) return { built: false };
    let inst = 0;
    for (const s of this.instSets) inst += s.mesh ? s.mesh.count : 0;
    return {
      blocks: this.blocks.length,
      activeBlocks: this._activeCount,
      detailBlocks: this._nearCount,
      contactShadows: this.props.sets.contact ? this.props.sets.contact.total : 0,
      lots: this.lotStats.lots,
      commercial: this.lotStats.commercial,
      staircases: this.lotStats.stairs,
      balconies: this.lotStats.balconies,
      windows: this.lotStats.windows,
      signs: this.lotStats.signs,
      segments: this.graph.segmentList.length,
      instances: inst,
      instancedMeshes: this.instSets.length,
      colliders: this.collision.stats(),
      boundary: this.boundaryStats,
      park: { ...this.parkStats, lookout: this.lookout, vistaShells: this.vistaCount },
      downtown: {
        towers: this.lotStats.towers,
        towerStoreys: this.lotStats.towerStoreys,
        shells: this.shellCount,
        // False means `look` had not registered the curtain-wall keys when this built, and the
        // quadrant is standing in plain stone. Surfaced so a miss is visible in one query
        // rather than as a mystery in a screenshot.
        materials: this.towerMats.real,
      },
      spawn: {
        pothole: this._spawns.pothole.length,
        traffic: this._spawns.traffic.length,
        ped: this._spawns.ped.length,
        cone: this._spawns.cone.length,
      },
      bootstrapLighting: !!this._bootstrapLights,
    };
  }

  /** Nothing in `world` reads a wall clock; visuals are a pure function of camera + sim. */
  freezeClocks() { this._frozen = true; }

  // ================================================================== teardown

  _teardown() {
    if (this.root) {
      this.ctx.scene.remove(this.root);
      this.root = null;
    }
    for (const g of this._geoms) g.dispose();
    this._geoms.length = 0;
    for (const v of this.stairVariants) v.geometry.dispose();
    for (const v of this.balconyVariants) v.geometry.dispose();
    for (const v of this.stairVariantsFar) v.geometry.dispose();
    for (const v of this.balconyVariantsFar) v.geometry.dispose();
    this.stairVariants = [];
    this.balconyVariants = [];
    this.stairVariantsFar = [];
    this.balconyVariantsFar = [];
    for (const s of this.instSets) if (s.mesh) s.mesh.geometry.dispose();
    this.instSets = [];
    /**
     * §7.2b: a reset that leaks shows up as unbounded `heapGrowthMB`. `_build()` mints a fresh
     * set of `instanceOf` clones every time it runs (the five vertex-coloured masonry families,
     * the two signage clones, the ground decals and — new in round 7 — the contact-shadow
     * material), and until now the array they were pushed into was only emptied by `dispose()`,
     * which the harness never calls between shots. `harness.reset()` calls `_teardown()`.
     */
    if (this.props && this.props.contactMat) this.props.contactMat.dispose();
    for (const m of this._materials) m.dispose();
    this._materials.length = 0;
    for (const t of this._textures) t.dispose();
    this._textures.length = 0;
    this._winterSets = null;
    this._summerSets = null;
    this._corniceSnow = [];
    this._winterOn = null;
    this._built = false;
  }

  dispose() {
    this._teardown();
    for (const m of this._materials) m.dispose();
    this._materials.length = 0;
    for (const t of this._textures) t.dispose();
    this._textures.length = 0;
    // The atlas deliberately survives _teardown() (see _build); this is where it actually goes.
    if (this._atlasTex) { this._atlasTex.dispose(); this._atlasTex = null; }
    this.atlas = null;
    if (this._bootstrapLights) {
      this.ctx.scene.remove(this._bootstrapLights);
      this._bootstrapLights = null;
    }
    if (this.ctx.world === this.query) this.ctx.world = null;
  }
}

/**
 * WINDOW OPENING — a glazed pane set BEHIND a projecting frame, so the opening reads as a hole
 * in a wall rather than as a sticker on one.
 *
 * ROUND-11. "Windows and sills are extruded slabs FLOATING IN FRONT OF the wall plane with
 * visible gaps and zero contact shadow." The old geometry tried to recess into the wall — the
 * box ran from local x = −1 (pane) to x = 0 (mouth) with the caller placing the mouth 2 cm
 * proud — and that can never work here, because the masonry is a solid extruded prism with no
 * opening cut in it. There was nothing for a reveal to be recessed INTO, so all that survived
 * was a rim floating a couple of centimetres off a flat wall.
 *
 * The construction below cannot suffer from that, by design: EVERY vertex lies at local
 * x ∈ [0, 1], the caller places local x = 0 exactly ON the wall plane, and local +X is the
 * outward face normal scaled by `building.windowReveal`. So the frame's back edge meets the
 * masonry with no gap and no z-fight, and the pane sits ~0.9 of the frame depth behind the
 * frame's front rim. That is the way a brick opening actually works — the brick face is proud
 * of the sash, not the other way round — and it gives `look`'s depth-curvature AO a 0.12 m
 * crease to bite on, which is inside its 0.22 m near sample radius.
 *
 *   head band  — front rim + SOFFIT (a downward face directly over the glass: the shadow line)
 *   sill band  — front rim + TOP    (an upward face directly under it: the lit line)
 *   jamb fins  — one inward face each, so the opening has vertical reveal edges too
 *
 * The head band's top and the sill band's underside are deliberately absent: the pale stone
 * lintel and sill trim (buildings.js `_windowRow`) are wider AND project further, so they cap
 * both. 12 tris → 14.
 *
 * UVs: the pane takes the whole window texture; every frame face is pinned into the texture's
 * top-left corner, which `TextureLab.windowGrid` fills with `WINDOW.mullion` before it draws a
 * single pane — so the frame is sash-frame colour by day and, because the emissive map is
 * black there, it does not glow at night.
 *
 * `coarse` is the far LOD: the pane alone, at the same depth, so the swap is invisible.
 */
const WIN_FRAME_UV = [[0.006, 0.006], [0.006, 0.006], [0.006, 0.006], [0.006, 0.006]];
function windowGeo(coarse = false) {
  const mb = new MeshBuilder(false);
  const px = 0.14;            // pane stand-off, in frame depths — a few mm of real world
  const ky = 0.075;           // head/sill band width, as a fraction of the opening height
  const kz = 0.10;            // and of the opening width
  const zo = 0.5 + kz, yt = 0.5 + ky, yb = -0.5 - ky;

  mb.quad([px, -0.5, 0.5], [px, -0.5, -0.5], [px, 0.5, -0.5], [px, 0.5, 0.5]);            // pane
  if (coarse) return mb.toGeometry('window:far');

  // head: front rim, then the soffit over the glass
  mb.quad([1, 0.5, zo], [1, 0.5, -zo], [1, yt, -zo], [1, yt, zo], WIN_FRAME_UV);
  mb.quad([0, 0.5, -zo], [1, 0.5, -zo], [1, 0.5, zo], [0, 0.5, zo], WIN_FRAME_UV);
  // sill: front rim, then the weathering top under the glass
  mb.quad([1, yb, zo], [1, yb, -zo], [1, -0.5, -zo], [1, -0.5, zo], WIN_FRAME_UV);
  mb.quad([0, -0.5, zo], [1, -0.5, zo], [1, -0.5, -zo], [0, -0.5, -zo], WIN_FRAME_UV);
  // jamb fins, facing into the opening
  mb.quad([1, -0.5, 0.5], [0, -0.5, 0.5], [0, 0.5, 0.5], [1, 0.5, 0.5], WIN_FRAME_UV);
  mb.quad([0, -0.5, -0.5], [1, -0.5, -0.5], [1, 0.5, -0.5], [0, 0.5, -0.5], WIN_FRAME_UV);
  return mb.toGeometry('window');
}
